/*
 * Carbisdale - LinePositionMap
 *
 * Pixel-Positionen fuer proportionale Zeilen.
 * Ermoeglicht Mouse-Hit-Testing und Cursor-Positionierung.
 * Wird von EditorView und CoreView gemeinsam genutzt.
 */

import { computeStringLayout } from './GlyphMetrics.js'
import { buildMonoShiftedPositions } from './textGeometry.js'
import { STYLES } from './config.js'

/**
 * Pixel-Positionen fuer alle Zeichen einer proportionalen Zeile.
 * Ermoeglicht Mouse-Hit-Testing und Cursor-Positionierung.
 */
export class LinePositionMap {
    constructor(charCount) {
        this.xPositions = new Float32Array(charCount)  // x-Start jedes Zeichens (skaliert)
        this.widths = new Float32Array(charCount)       // Breite jedes Zeichens (skaliert)
        this.totalWidth = 0                             // Gesamt-Breite (skaliert)
    }

    /**
     * Findet den Zeichenindex an einer Pixel-X-Position.
     * Klick links der Mitte → vor dem Zeichen, rechts → nach dem Zeichen.
     */
    getCharIndexAtX(pixelX) {
        if (this.xPositions.length === 0) return 0
        if (pixelX <= 0) return 0
        if (pixelX >= this.totalWidth) return this.xPositions.length

        let lo = 0, hi = this.xPositions.length - 1

        while (lo <= hi) {
            const mid = (lo + hi) >>> 1
            // Low Surrogate (width=0) überspringen → auf High Surrogate zurückgehen
            let effMid = mid
            if (this.widths[effMid] === 0 && effMid > 0) effMid--
            const midX = this.xPositions[effMid]
            // Gesamtbreite: High Surrogate (die Glyph-Breite)
            const midEnd = midX + this.widths[effMid]

            if (pixelX < midX) {
                hi = effMid - 1
            } else if (pixelX >= midEnd) {
                // Nach Surrogate-Paar: Index nach dem Low Surrogate
                lo = (this.widths[effMid + 1] === 0 ? effMid + 2 : effMid + 1)
            } else {
                // Treffer: in welcher Haelfte des Zeichens?
                const midPoint = midX + this.widths[effMid] / 2
                // Vor dem Zeichen → High Surrogate Index, nach dem Zeichen → nach Low Surrogate
                if (pixelX < midPoint) return effMid
                return (effMid + 1 < this.xPositions.length && this.widths[effMid + 1] === 0)
                    ? effMid + 2  // Überspringe Low Surrogate
                    : effMid + 1
            }
        }

        return lo
    }

    /**
     * Findet den Index des Zeichens, dessen Pixel-Spanne pixelX enthält
     * (KEINE Mitte-Rundung wie getCharIndexAtX). Für Hit-Testing einzelner
     * Glyphen — z.B. Links: getCharIndexAtX liefert die Cursor-Einfügeposition
     * an der nächsten Zeichengrenze, wodurch die Link-Trefferzone eine halbe
     * Zeichenbreite nach links verrutscht. Diese Methode trifft das Zeichen
     * direkt unter der Maus.
     * @param {number} pixelX - Pixel-Position relativ zum Zeilen-Textstart
     * @returns {number} Index des getroffenen Zeichens, oder -1 wenn außerhalb
     */
    getCharIndexUnderX(pixelX) {
        if (this.xPositions.length === 0) return -1
        if (pixelX < 0 || pixelX >= this.totalWidth) return -1
        // Lineare Suche reicht — wird nur bei Hover/Klick gerufen, Zeilen sind kurz.
        for (let i = 0; i < this.xPositions.length; i++) {
            if (this.widths[i] === 0) continue  // Low Surrogate überspringen
            if (pixelX >= this.xPositions[i] && pixelX < this.xPositions[i] + this.widths[i]) {
                return i
            }
        }
        return -1
    }
}

/**
 * Baut eine LinePositionMap aus einem Slice geparster Zeichen.
 *
 * Wenn `monoCharWidth` gegeben ist, werden die Pixel-Positionen mono-shifted
 * (siehe `buildMonoShiftedPositions`) — noetig fuer Inline-Code-Spans bzw. fuer
 * Styles mit `isMono: true` (`EDITOR_CODE`, `CONSOLE_AI_CODE`). Ohne den Param
 * bleiben die Positionen rein proportional (Backwards-Compat fuer Aufrufer
 * ohne Mono-Bereiche).
 *
 * @param {string} lineText - Text der Zeile (Display-Zeichen)
 * @param {Array<{start, end, bold, italic, mono?, style?}>} formatRuns - Format-Runs (relativ zum Zeilen-Start)
 * @param {GlyphMetricsCache} metricsCache
 * @param {number} scaleFactor
 * @param {Object} [precomputedLayout] - Vorberechnetes Layout von computeStringLayout() (optional, vermeidet Doppelberechnung)
 * @param {number} [monoCharWidth] - Mono-Glyph-Breite bei aktueller heightQ. Wenn gegeben, werden Positionen mono-shifted.
 * @returns {LinePositionMap}
 */
export function buildLinePositionMap(lineText, formatRuns, metricsCache, scaleFactor, precomputedLayout, monoCharWidth = null) {
    const utf16Len = lineText.length
    const map = new LinePositionMap(utf16Len)
    if (utf16Len === 0) return map

    const layout = precomputedLayout || computeStringLayout(lineText, metricsCache, metricsCache.baseStep, formatRuns)

    // Mono-aware Positionen, falls monoCharWidth uebergeben. effLeft ist
    // bereits skaliert (startX=0) und enthaelt den kumulativen monoShift.
    const cpLen = layout.positions.length
    const effLeft = monoCharWidth != null
        ? buildMonoShiftedPositions(layout, formatRuns, STYLES, scaleFactor, monoCharWidth, cpLen, 0)
        : null

    // Layout ist codepoint-indiziert, posMap muss UTF-16-indiziert sein
    // Surrogate-Paare: High Surrogate bekommt Position+Breite, Low Surrogate Breite 0
    let cpIdx = 0
    for (let i = 0; i < utf16Len; i++) {
        map.xPositions[i] = effLeft != null ? effLeft[cpIdx] : layout.positions[cpIdx] * scaleFactor
        const code = lineText.charCodeAt(i)
        if (code >= 0xD800 && code <= 0xDBFF && i + 1 < utf16Len) {
            // High Surrogate: volle Breite zuweisen
            map.widths[i] = layout.widths[cpIdx] * scaleFactor
            // Low Surrogate: gleiche Position, Breite 0
            i++
            map.xPositions[i] = effLeft != null ? effLeft[cpIdx] : layout.positions[cpIdx] * scaleFactor
            map.widths[i] = 0
        } else {
            map.widths[i] = layout.widths[cpIdx] * scaleFactor
        }
        cpIdx++
    }
    map.totalWidth = effLeft != null ? effLeft[cpLen] : layout.totalWidth * scaleFactor

    return map
}

/**
 * Reichert formatRuns mit bold/italic aus Style-Definitionen an.
 * Styles wie EDITOR_HEADING_1 definieren isBold in STYLES, aber parsedToFormatRuns
 * erkennt das nicht ueber den Style-Namen — hier wird es korrigiert.
 */
export function enrichFormatRuns(formatRuns) {
    if (!formatRuns || formatRuns.length === 0) return formatRuns
    let needsEnrichment = false
    for (const run of formatRuns) {
        if (run.style) {
            // Compound-Style: Base-Teil extrahieren
            const baseName = run.style.includes('+') ? run.style.split('+')[0] : run.style
            const styleDef = STYLES[baseName]
            if (styleDef && ((styleDef.isBold && !run.bold) || (styleDef.isItalic && !run.italic))) {
                needsEnrichment = true
                break
            }
        }
    }
    if (!needsEnrichment) return formatRuns
    return formatRuns.map(run => {
        if (!run.style) return run
        const baseName = run.style.includes('+') ? run.style.split('+')[0] : run.style
        const styleDef = STYLES[baseName]
        if (!styleDef) return run
        return { ...run, bold: run.bold || styleDef.isBold, italic: run.italic || styleDef.isItalic }
    })
}
